import React, { useEffect, useState } from 'react'
import { useCertificateStore } from '@store'
import { Plus, Eye, Edit2, Trash2, Archive, RotateCcw, Award, TrendingUp, Package as PackageIcon, Filter, ArrowUpDown } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import CertificateForm from './CertificateForm'
import CertificateView from './CertificateView'
import './Certificates.css'

/**
 * Icon mapping for certificate icons
 */
import {
	Award as AwardIconComp,
	Shield,
	CheckCircle,
	Lock,
	UserCheck,
	UserLock,
	Leaf,
	Heart,
	Hospital,
	CreditCard,
	Flag,
	CloudUpload,
	Cloud,
	Pill,
	RefreshCw,
} from 'lucide-react'

const iconMap = {
	award: AwardIconComp,
	'shield-alt': Shield,
	'check-circle': CheckCircle,
	lock: Lock,
	'user-shield': UserCheck,
	'user-lock': UserLock,
	leaf: Leaf,
	heartbeat: Heart,
	hospital: Hospital,
	'credit-card': CreditCard,
	'flag-usa': Flag,
	'cloud-upload-alt': CloudUpload,
	cloud: Cloud,
	'cloud-lock': Cloud,
	pills: Pill,
	'sync-alt': RefreshCw,
	'file-signature': AwardIconComp,
	'hard-hat': Shield,
}

/**
 * Get icon component for certificate
 */
const getCertificateIcon = (iconName) => {
	const IconComponent = iconMap[iconName] || AwardIconComp
	return <IconComponent size={20} />
}

/**
 * Certificates Management Page (Super Admin Only)
 *
 * Complete certificate management interface with:
 * - List view with DataTable
 * - Search and filtering
 * - Create/Edit/View/Delete operations
 * - Statistics dashboard
 * - Pagination
 * - Responsive design
 *
 * Features:
 * - Full CRUD operations
 * - Real-time search
 * - Status filtering
 * - Soft delete with restore
 * - Confirmation dialogs
 * - Loading states
 * - Empty states
 * - Error handling
 */
const Certificates = () => {
	const {
		certificates,
		loading,
		filters,
		pagination,
		stats,
		fetchCertificates,
		selectCertificate,
		deleteCertificate,
		restoreCertificate,
		setSearch,
		setStatusFilter,
		setPage,
		nextPage,
		prevPage,
	} = useCertificateStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null) // 'delete', 'restore'
	const [selectedCertificateForAction, setSelectedCertificateForAction] = useState(null)

	// Fetch certificates on mount
	useEffect(() => {
		fetchCertificates()
	}, [])

	/**
	 * Handle create certificate
	 */
	const handleCreate = () => {
		selectCertificate(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view certificate
	 */
	const handleView = (cert) => {
		selectCertificate(cert)
		setShowViewModal(true)
	}

	/**
	 * Handle edit certificate
	 */
	const handleEdit = (cert) => {
		selectCertificate(cert)
		setShowEditModal(true)
	}

	/**
	 * Handle delete certificate
	 */
	const handleDelete = (cert) => {
		setSelectedCertificateForAction(cert)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore certificate
	 */
	const handleRestore = (cert) => {
		setSelectedCertificateForAction(cert)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Confirm action (delete/restore)
	 */
	const confirmAction = async () => {
		if (!selectedCertificateForAction) return

		try {
			if (actionType === 'delete') {
				await deleteCertificate(selectedCertificateForAction.id)
			} else if (actionType === 'restore') {
				await restoreCertificate(selectedCertificateForAction.id)
			}

			setShowConfirmDialog(false)
			setSelectedCertificateForAction(null)
			setActionType(null)
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedCertificateForAction(null)
		setActionType(null)
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status, deletedAt) => {
		if (deletedAt || status === 'archived') {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	// Define table columns
	const columns = [
		{
			key: 'icon',
			label: 'Icon',
			align: 'center',
			render: (cert) => (
				<div className="certificate-icon-cell">{getCertificateIcon(cert.icon)}</div>
			),
		},
		{
			key: 'title',
			label: 'Certificate',
			sortable: true,
			render: (cert) => (
				<div className="certificate-cell">
					<div className="certificate-cell__title">{cert.title}</div>
					<div className="certificate-cell__slug">{cert.slug}</div>
				</div>
			),
		},
		{
			key: 'description',
			label: 'Description',
			render: (cert) => (
				<div className="description-cell">
					{cert.description
						? cert.description.substring(0, 80) + (cert.description.length > 80 ? '...' : '')
						: '-'}
				</div>
			),
		},
		{
			key: 'packages_count',
			label: 'Usage',
			sortable: true,
			align: 'center',
			render: (cert) => `${cert.packages_count || 0} packages`,
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (cert) => getStatusBadge(cert.status, cert.deleted_at),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (cert) => !cert.deleted_at && cert.status !== 'archived',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (cert) => !cert.deleted_at && cert.status !== 'archived',
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (cert) => cert.deleted_at || cert.status === 'archived',
		},
	]

	return (
		<div className="certificates-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="Certificates"
				description="Manage compliance standards and certifications"
				breadcrumbs={[{ label: 'Management' }, { label: 'Certificates' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Add Certificate</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Award size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Certificates</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<TrendingUp size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active Certificates</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--info">
						<PackageIcon size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">In Use</div>
						<div className="stat-card__value">{stats.inUse}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<Archive size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Archived</div>
						<div className="stat-card__value">{stats.archived}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search certificates..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={certificates}
				actions={actions}
				loading={loading}
				emptyMessage="No certificates found. Create your first certificate to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && <CertificateForm mode="create" onClose={() => setShowCreateModal(false)} />}

			{/* Edit Modal */}
			{showEditModal && <CertificateForm mode="edit" onClose={() => setShowEditModal(false)} />}

			{/* View Modal */}
			{showViewModal && (
				<CertificateView
					certificateData={certificates.find((c) => c.id === useCertificateStore.getState().selectedCertificate?.id)}
					onClose={() => setShowViewModal(false)}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
				/>
			)}

			{/* Confirm Dialog (Delete/Restore) */}
			{showConfirmDialog && selectedCertificateForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={actionType === 'delete' ? 'Delete Certificate?' : 'Restore Certificate?'}
					message={
						actionType === 'delete'
							? `Are you sure you want to delete "${selectedCertificateForAction.title}"? This will archive it and mark it as deleted, but data will be preserved.`
							: `Are you sure you want to restore "${selectedCertificateForAction.title}"? This will make it active and available for selection again.`
					}
					confirmText={actionType === 'delete' ? 'Delete' : 'Restore'}
					variant={actionType === 'delete' ? 'danger' : 'info'}
				/>
			)}
		</div>
	)
}

export default Certificates
